import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId, goToAudit } from "@/lib/scroll";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "services", label: "Services" },
  { id: "results", label: "Results" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

export const Logo = ({ onClick }) => (
  <button
    type="button"
    data-testid="logo-button"
    onClick={onClick}
    aria-label="Viral Pulse Agency — home"
    className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded-lg"
  >
    <img src="/assets/vp-wordmark.png" alt="Viral Pulse Agency" className="h-10 w-auto sm:h-11" />
  </button>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
          scrolled
            ? "border-white/10 bg-[#05050A]/85 backdrop-blur-xl"
            : "border-transparent bg-transparent"
        }`}
      >
        <nav className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <Logo onClick={() => go("home")} />
          <div className="hidden items-center gap-9 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                type="button"
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="group relative text-[13px] font-bold uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-white"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-purple-500 transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="nav-audit-cta"
              onClick={goToAudit}
              className="group hidden items-center gap-1.5 rounded-full bg-purple-600 px-6 py-3 text-[13px] font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-purple-500 sm:flex"
            >
              Free Marketing Audit
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              type="button"
              data-testid="mobile-menu-button"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#05050A]/97 px-8 backdrop-blur-2xl lg:hidden"
          >
            <div className="space-y-3">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  type="button"
                  data-testid={`mobile-nav-link-${l.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                  onClick={() => go(l.id)}
                  className="block py-1 font-display text-4xl font-extrabold tracking-tight text-white transition-colors hover:text-purple-400"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
            <motion.button
              type="button"
              data-testid="mobile-nav-audit-cta"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.4 }}
              onClick={goToAudit}
              className="mt-10 flex w-fit items-center gap-2 rounded-full bg-purple-600 px-8 py-4 text-sm font-bold uppercase tracking-wide text-white"
            >
              Free Marketing Audit
              <ArrowUpRight className="h-5 w-5" />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
